<template>
  <section class="section">
    <div class="container">
      <Title>
        Applications
        <HeaderIcon
          v-if="!downloading"
          @click="download()"
          icon="mdi-file-download"
          tooltip="Download CSV"
        ></HeaderIcon>
        <span v-if="downloading" class="spinner small"></span>
      </Title>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="true"
        @submit="getApplications($event)"
        :minLength="3"
      ></SearchBar>

      <p v-show="!searching && items.length === 0 && hasSearchedFirstTime">
        <i>No applications.</i>
      </p>

      <p v-show="!hasSearchedFirstTime" class="notification is-primary">
        Search for the application you are looking for.
      </p>

      <table
        class="table is-hoverable is-fullwidth is-multirow"
        v-if="firstLoadDone && items.length !== 0"
      >
        <thead>
          <tr class="is-unselectable">
            <th @click="handleOrder('name')">Name</th>
            <th @click="handleOrder('version')">Version</th>
            <th @click="handleOrder('hasLicense')">Licenses</th>
            <th @click="handleOrder('installed')">Installed</th>
            <th @click="handleOrder('notInstalled')">Uninstalled</th>
          </tr>
        </thead>
        <TableEffect :skipGroupTransition="true">
          <tr
            v-for="item of filtered"
            :key="key(item)"
            @click="selectItem(item)"
            class="clickable"
          >
            <td>
              {{ item.name }}
            </td>
            <td>{{ item.version }}</td>
            <td>
              <span
                class="tag"
                :class="{ 'is-primary': item.hasLicense !== 0 }"
                >{{ item.hasLicense }}</span
              >
            </td>
            <td>
              <span
                class="tag"
                :class="{ 'is-primary': item.installed !== 0 }"
                >{{ item.installed }}</span
              >
            </td>
            <td>
              <span
                class="tag"
                :class="{ 'is-primary': item.notInstalled !== 0 }"
                >{{ item.notInstalled }}</span
              >
            </td>
          </tr>
        </TableEffect>
      </table>
      <Paging
        v-if="items && items.length > 0"
        :items="paging.pages"
        :selected="paging.page"
        @change="setPage($event)"
        @size="setPageMax($event)"
        :size="paging.max"
        :hasOptions="true"
        autosave="computersApplications"
      ></Paging>
    </div>

    <vue-context ref="menu">
      <template slot-scope="child">
        <li>
          <a @click.prevent="toggleRename(child.data)">Edit</a>
        </li>
      </template>
    </vue-context>

    <Info
      v-if="showInfo && selectedItem"
      v-model="showInfo"
      :item="selectedItem"
    ></Info>
  </section>
</template>

<style lang="css" scoped>
th {
  font-weight: normal;
}
</style>

<script>
import "vue-context/src/sass/vue-context.scss";
import { getNumbers, getStartIndex } from "@/utils/paging";

import { toReadableDate } from "@/utils/date";

import { postApi } from "@/utils/http";
import { focus, getId, downloadFile, getTextUrl } from "@/utils/document";
import { _order } from "@/utils/order";

export default {
  components: {
    TableEffect: () => import("@/components/singles/TableEffect.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    HeaderIcon: () => import("@/components/main/HeaderIcon.vue"),
    Paging: () => import("@/components/main/paging/Nav.vue"),
    Info: () => import("@/components/main/application/Info.vue"),
    LoaderOne: () => import("@/components/singles/LoaderOne.vue"),
    VueContext: () => import("vue-context"),
  },

  computed: {
    filtered() {
      return this.items.slice(
        this.paging.start,
        this.paging.page * this.paging.max
      );
    },
    key() {
      return (item) => {
        return item.name + item.version + item.installed;
      };
    },
  },
  data() {
    return {
      items: [],
      allItems: [],
      rawItems: [],
      selectedItem: null,
      showInfo: false,
      firstLoadDone: false,
      downloading: false,
      searching: false,
      tempFilter: "",
      filter: "",
      saving: false,
      searchId: getId(),
      hasSearchedFirstTime: false,
      paging: {
        page: 1,
        max: 15,
        start: 0,
        pages: [],
      },
    };
  },
  async created() {
    try {
      this.firstLoadDone = true;
    } catch (err) {
      console.log(err);
      this.$helpers.notify("Error", "Could not get data.", "danger", 5000);
    }
  },
  beforeDestroy() {},
  methods: {
    handleOrder(path) {
      this.items = this.order(this.items, path);
    },
    order(items, path) {
      const stateOrder = this.$store.state.settings.user.computersApplications
        ?.order;
      let reversed = false;

      if (stateOrder?.path === path) reversed = !stateOrder.reversed;

      this.$store.dispatch("settings/setUser", {
        path: "computersApplications",
        key: "order",
        value: { path, reversed },
      });

      return _order(items, { path, reversed });
    },
    setPageMax(size) {
      this.paging.max = size;

      this.setPage(1);
      this.setPages(this.items);
    },
    setPage(page) {
      this.paging.start = getStartIndex(page, this.paging.max);
      this.paging.page = page;
    },
    focus: focus,
    async getApplications(searchStr) {
      this.hasSearchedFirstTime = true;
      this.searching = true;

      await this.$store.dispatch("settings/getUserSetting", {
        path: "computersApplications",
        key: "order",
      });

      const stateOrder = this.$store.state.settings.user.computersApplications
        ?.order;

      const res = await postApi("get/computersApplicationsSearch", {
        domainUID: this.$route.params.domainUID,
        query: searchStr,
      });

      const paging = this.$store.state.settings.user.computersApplications
        ?.paging;
      if (paging && paging.max) this.paging.max = paging.max;

      const transformed = _order(this.transform(res[0]), stateOrder);
      this.setPages(transformed);
      this.items = transformed;
      this.allItems = transformed;
      this.rawItems = res[0];

      this.searching = false;
      focus(this.searchId);
    },

    setPages(items) {
      this.paging.pages = getNumbers(items, this.paging.max);
    },

    toggleRename(item) {
      const index = this.items.findIndex((x) => item == x);
      this.$set(this.items, index, { ...item, ...{ edit: !item.edit } });
    },

    async download() {
      this.downloading = true;
      let text = `Computer;Name;Version;License;Installed;Date\r\n`;

      this.rawItems.map(
        (x) =>
          x &&
          x.application &&
          (text += `${x.computer.name || ""};${x.application.name || ""};${x
            .application.version || ""};${x.application.key || ""};${
            x.application.installed ? "Yes" : "No"
          };${
            x.application.timeChanged
              ? toReadableDate(x.application.timeChanged)
              : ""
          };\r\n`)
      );

      downloadFile(
        getTextUrl(text),
        `${this.$route.params.domainUID}_computers_applications.csv`
      );
      this.downloading = false;
    },

    selectItem(item) {
      const items = this.rawItems.filter(
        (x) =>
          x &&
          x.application &&
          x.application.name === item.name &&
          x.application.version === item.version
      );

      this.selectedItem = { application: item, items: items };
      this.showInfo = true;
    },

    transform(items) {
      let newArr = [];

      for (const item of items) {
        if (item && item.application) {
          const existingItem = newArr.find(
            (x) =>
              x.name === item.application.name &&
              x.version === item.application.version
          );

          if (!existingItem) {
            const all = items.filter(
              (x) =>
                x.application &&
                x.application.name === item.application.name &&
                x.application.version === item.application.version
            );

            const installed = all.filter(
              (x) => x.application && x.application.installed
            ).length;
            const hasLicense = all.filter(
              (x) =>
                x.application && x.application.key && x.application.key !== ""
            ).length;

            newArr.push({
              installed: installed,
              notInstalled: all.length - installed,
              hasLicense: hasLicense,
              name: item.application.name,
              version: item.application.version,
              id: item.application.id,
            });
          }
        }
      }

      return newArr;
    },
  },
};
</script>
